import { useState, useRef, useEffect } from 'react';
import './Navbar.css';
import oneKosmosIcon from '../../assets/images/1kosmos-icon.png';

// Exported so MainLayout can pick the same first-accessible dashboard for
// the post-login redirect that this order already gives the nav menu itself
// (visibleNav[0]) — one declared order for both instead of two lists that
// could drift apart.
export const NAV = [
    { id: 'pipeline', href: '/pipeline',label: 'Pipeline Dashboard' },
    { id: 'arr',      href: '/arr',     label: 'ARR Dashboard' },
    { id: 'ar',       href: '/ar',      label: 'A/R Dashboard'  },
    { id: 'legal',    href: '/legal',   label: 'Legal Dashboard' },
    { id: 'devops', href: '/devops', label: 'Dev Ops Dashboard' },
    { id: 'cs', href: '/cs', label: 'CS Dashboard' },
    { id: 'pm', href: '/pm', label: 'Product Management Dashboard' },
    { id: 'rnd', href: '/rnd', label: 'R&D Dashboard' },
    { id: 'nam', href: '/nam', label: 'Sales NAM Dashboard' },
    { id: 'apac', href: '/apac', label: 'Sales APAC Dashboard' },
    { id: 'sol', href: '/sol', label: 'Sales Solution Dashboard' },
    { id: 'marketing', href: '/marketing', label: 'Marketing Dashboard' },
    { id: 'ga',  href: '/ga',  label: 'G&A Dashboard' },
];

// G&A/Marketing/R&D/CS/Dev Ops/Product Management/Sales APAC/Sales NAM/
// Sales Solution live under one "Department Dashboard" nav item — a single
// top-level link (not a dropdown) that lands on /department, which renders
// its own sub-tab bar for picking which of the nine to view (see
// DepartmentMain.js). NAV itself stays a flat, declared-order list
// (MainLayout's "/" redirect and access-control logic both key off that);
// this only controls how Navbar visually renders a subset of it.
export const DEPARTMENT_IDS = ['devops', 'cs', 'pm', 'rnd', 'nam', 'apac', 'sol', 'marketing', 'ga'];

function elapsedTimer(from, now) {
    if (!from) return '';
    const totalSeconds = Math.max(0, Math.floor((now - from) / 1000));
    if (totalSeconds < 5) return 'Just Now';
    if (totalSeconds < 60) return `${totalSeconds}s ago`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes < 60) return `${minutes}m ${String(seconds).padStart(2, '0')}s ago`;
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    return `${hours}h ${String(remMinutes).padStart(2, '0')}m ago`;
}

// A fresh query param forces the browser to treat this as a new URL, so it
// can't be served from an index.html cached under the old URL — the usual
// cause of "still shows the old version" reports right after a deploy.
function hardRefresh() {
    const url = new URL(window.location.href);
    url.searchParams.set('_r', Date.now().toString());
    window.location.href = url.toString();
}

function Navbar({ active = 'arr', user, onLogout, lastRefreshed }) {
    const displayName = user?.name || user?.username || 'User';
    const email       = user?.email || '';
    const initial     = displayName.trim().charAt(0).toUpperCase() || 'U';
    const access      = user?.isSuperuser ? NAV.map((item) => item.id) : (user?.access || []);
    const visibleNav  = NAV.filter((item) => access.includes(item.id));
    const topNav      = visibleNav.filter((item) => !DEPARTMENT_IDS.includes(item.id));
    const hasDeptAccess = DEPARTMENT_IDS.some((id) => access.includes(id));
    // 'department' is MainLayout's active value for the grouped page; the
    // individual department ids still arrive here when a standalone
    // /ga, /marketing, /rnd, or /cs deep link is open directly.
    const deptActive  = active === 'department' || DEPARTMENT_IDS.includes(active);

    const [open, setOpen] = useState(false);
    const dropRef = useRef(null);

    // Ticks every second like a real timer — lastRefreshed itself only
    // changes when a dashboard actually finishes a fresh data fetch.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!lastRefreshed) return;
        const tick = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(tick);
    }, [lastRefreshed]);

    const go = (event, href) => {
        event.preventDefault();
        if (window.location.pathname === href) return;
        window.history.pushState(null, '', href);
        window.dispatchEvent(new PopStateEvent('popstate'));
    };

    useEffect(() => {
        if (!open) return;
        const close = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    return (
        <nav className="navbar">
            <a className="nb-logo" href={visibleNav[0]?.href || '/settings'} onClick={(event) => go(event, visibleNav[0]?.href || '/settings')}>
                <img className="nb-brand-icon" src={oneKosmosIcon} alt="1Kosmos" />
                <div className="nb-logo-text">
                    <span className="nb-name">1Kosmos</span>
                </div>
            </a>

            <ul className="nb-links">
                {topNav.map(n => (
                    <li key={n.id}>
                        <a href={n.href} onClick={(event) => go(event, n.href)} className={active === n.id ? 'active' : ''}>{n.label}</a>
                    </li>
                ))}
                {hasDeptAccess && (
                    <li>
                        <a href="/department" onClick={(event) => go(event, '/department')} className={deptActive ? 'active' : ''}>Department Dashboard</a>
                    </li>
                )}
            </ul>

            {lastRefreshed && <span className="nb-refresh" title={new Date(lastRefreshed).toLocaleString()}>Refreshed {elapsedTimer(lastRefreshed, now)}</span>}
            <button type="button" className="nb-refresh-btn" onClick={hardRefresh} title="Reload this page from the server — use this if the screen doesn't reflect a new deployment or the latest data">
                ↻ Refresh
            </button>

            <div className="nb-right" ref={dropRef}>
                <button
                    className="nb-avatar-btn"
                    onClick={() => setOpen(o => !o)}
                    aria-haspopup="true"
                    aria-expanded={open}
                >
                    <span className="nb-avatar">{initial}</span>
                </button>

                {open && (
                    <div className="nb-dropdown" role="menu">
                        <div className="nb-dd-profile">
                            <span className="nb-dd-avatar">{initial}</span>
                            <div className="nb-dd-meta">
                                <strong className="nb-dd-name">{displayName}</strong>
                                {email && <span className="nb-dd-email">{email}</span>}
                            </div>
                        </div>

                        <div className="nb-dd-divider" />

                        <div className="nb-dd-section">
                            <p className="nb-dd-section-label">Account</p>
                            <button
                                className={`nb-dd-item${window.location.pathname === '/profile' ? ' active' : ''}`}
                                onClick={(event) => { setOpen(false); go(event, '/profile'); }}
                            >
                                <span className="nb-dd-icon-wrap">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                                </span>
                                Your Profile
                            </button>
                            {(user?.isSuperuser || user?.access?.includes('administrator') || user?.access?.includes('developer')) && (
                                <button
                                    className={`nb-dd-item${window.location.pathname === '/settings' ? ' active' : ''}`}
                                    onClick={(event) => { setOpen(false); go(event, '/settings'); }}
                                >
                                    <span className="nb-dd-icon-wrap">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93A10 10 0 1 0 4.93 19.07"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/></svg>
                                    </span>
                                    Settings
                                </button>
                            )}
                        </div>

                        <div className="nb-dd-divider" />

                        <div className="nb-dd-footer">
                            <button
                                className="nb-dd-signout"
                                onClick={() => { setOpen(false); onLogout(); }}
                                role="menuitem"
                            >
                                <span className="nb-dd-icon-wrap nb-dd-icon-red">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                                </span>
                                Sign out
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
}

export default Navbar;
